import { useRef } from 'react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import GraphPaperTool from '../components/GraphPaperTool';
import ErrorBoundary from '../components/ErrorBoundary';
import usePageMeta from '../hooks/usePageMeta';
import { useReveal } from './pageShared';
import '../landing.css';

function escapeJson(s) {
  return typeof s === 'string' ? s.replace(/</g, '\\u003c').replace(/>/g, '\\u003e') : s;
}

function buildJsonLd(t) {
  const url = t.canonical;
  const faqs = (t.faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.a,
    },
  }));

  const graph = [
    {
      '@type': 'WebPage',
      '@id': url,
      url,
      name: t.metaTitle,
      description: t.metaDescription,
      inLanguage: 'en',
      isPartOf: { '@id': 'https://printablegraphpaperpdf.com/' },
      primaryImageOfPage: 'https://printablegraphpaperpdf.com/og-image.png',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://printablegraphpaperpdf.com/' },
        { '@type': 'ListItem', position: 2, name: t.h1.replace(/\.$/, ''), item: url },
      ],
    },
    ...(faqs.length ? [{ '@type': 'FAQPage', mainEntity: faqs }] : []),
  ];

  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph.map((g) => g) }, (k, v) => escapeJson(v));
}

function Section({ className = '', children, container = true }) {
  return (
    <section className={`lp-section ${className}`}>
      {container ? <div className="lp-container">{children}</div> : children}
    </section>
  );
}

function SectionHeader({ eyebrow, title, body }) {
  return (
    <div className="lp-section-head" data-reveal="true">
      {eyebrow && <p className="lp-eyebrow">{eyebrow}</p>}
      {title && <h2 className="lp-h2">{title}</h2>}
      {body && <p className="lp-sec-lead">{body}</p>}
    </div>
  );
}

export default function LandingPage({ template: t }) {
  const toolRef = useRef(null);
  const revealRef = useReveal();

  usePageMeta({
    title: t.metaTitle,
    description: t.metaDescription,
    keywords: t.keywords,
    canonical: t.canonical,
    ogTitle: t.metaTitle,
    ogDescription: t.metaDescription,
    ogUrl: t.ogUrl,
  });

  return (
    <ErrorBoundary>
      <div className="app lp-landing" ref={revealRef}>
        <SiteNav
          onPrint={() => toolRef.current?.handlePrint()}
          onDownload={(f) => toolRef.current?.handleDownload(f)}
        />

        <main className="lp-main">
          <header className="lp-hero">
            <div className="lp-container">
              <p className="lp-eyebrow page-rise page-d1">{t.eyebrow}</p>
              <h1 className="lp-title page-rise page-d2">{t.h1}</h1>
              {t.intro.map((p, i) => (
                <p className="lp-lead page-rise page-d3" key={i}>{p}</p>
              ))}
              <div className="lp-cta-row page-rise page-d3">
                <a className="lp-btn-primary" href="#lp-tool">{t.ctaPrimary}</a>
                <a className="lp-btn-secondary" href="#lp-uses">{t.secondaryCta}</a>
              </div>
              <div className="lp-facts page-rise page-d4">
                {t.facts.map((f) => (
                  <div className="lp-fact" key={f.k}>
                    <span className="lp-fact-k">{f.k}</span>
                    <span className="lp-fact-v">{f.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </header>

          <section className="lp-tool-section" id="lp-tool" aria-label="Graph paper generator">
            <div className="lp-tool-wrapper">
              <div className="lp-tool-frame">
                <div className="lp-tool-bar">
                  <span className="lp-tool-caption">LIVE GENERATOR</span>
                  <span className="lp-tool-hint">Adjust any setting — the preview updates instantly</span>
                </div>
                <GraphPaperTool ref={toolRef} initialSettings={t.preset} />
              </div>
            </div>
          </section>

          <Section className="lp-section-white">
            <SectionHeader eyebrow="Specs & printing" title={t.dimHeading} body={t.dimBody} />
            <div className="lp-spec-grid" data-reveal="true" data-stagger="true">
              {t.dims.map((d) => (
                <div className="lp-spec-card" key={d.k}>
                  <span className="lp-spec-k">{d.k}</span>
                  <span className="lp-spec-v">{d.v}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section className="lp-section-tint">
            <SectionHeader eyebrow="Grid sizes" title={t.sizesHeading} body={t.sizesBody} />
            <div className="lp-card-grid" data-reveal="true" data-stagger="true">
              {t.sizes.map((s) => (
                <a className="lp-card lp-card-link" href="#lp-tool" key={s.title}>
                  <h3 className="lp-card-title">{s.title}</h3>
                  <p className="lp-card-body">{s.body}</p>
                  <span className="lp-card-cta">{s.cta} →</span>
                </a>
              ))}
            </div>
          </Section>

          <Section className="lp-section-white">
            <SectionHeader eyebrow="Formats" title={t.formatHeading} body={t.formatBody} />
            <div className="lp-card-grid lp-formats" data-reveal="true" data-stagger="true">
              {t.formats.map((f) => (
                <div className="lp-card" key={f.name}>
                  <span className="lp-format-badge">{f.name}</span>
                  <h3 className="lp-card-title">{f.title}</h3>
                  <p className="lp-card-body">{f.body}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section className="lp-section-tint">
            <SectionHeader eyebrow="Orientation" title={t.orientHeading} body={t.orientBody} />
            <div className="lp-orient-grid" data-reveal="true" data-stagger="true">
              {t.orientPoints.map((p, i) => (
                <div className="lp-orient-row" key={i}>
                  <span className="lp-check" aria-hidden="true">✓</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section>
            <SectionHeader eyebrow="Use cases" title={t.usesHeading} body={t.usesBody} />
            <div className="lp-card-grid" id="lp-uses" data-reveal="true" data-stagger="true">
              {t.uses.map((u) => (
                <div className="lp-card" key={u.title}>
                  <h3 className="lp-card-title">{u.title}</h3>
                  <p className="lp-card-body">{u.body}</p>
                </div>
              ))}
            </div>
          </Section>

          <section className="faq-section lp-faq">
            <div className="faq-inner">
              <SectionHeader eyebrow="FAQ" title={t.faqHeading} />
              <div className="faq-list" data-reveal="true" data-stagger="true">
                {t.faqs.map((f) => (
                  <details className="faq-item" key={f.q}>
                    <summary className="faq-question">{f.q}</summary>
                    <div className="faq-answer">
                      <p>{f.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <Section className="lp-section-white">
            <SectionHeader eyebrow="Explore" title={t.relatedHeading} />
            <div className="lp-related-grid" data-reveal="true" data-stagger="true">
              {t.related.map((r) => (
                <a className="lp-related-card" href={r.path} key={r.path}>
                  <span className="lp-related-title">{r.title}</span>
                  <span className="lp-related-body">{r.blurb}</span>
                  <span className="lp-related-go">Open generator →</span>
                </a>
              ))}
            </div>
          </Section>
        </main>

        <SiteFooter />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: buildJsonLd(t) }}
        />
      </div>
    </ErrorBoundary>
  );
}